import React, { useState } from 'react';
import styled from 'styled-components';

interface ConnectByIDFormProps {
  onClose: () => void;
  onSubmit: (athleteId: string) => void;
  error?: string | null;
  isConnecting?: boolean;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 3rem;
  animation: fadeIn 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem;
    align-items: center;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: var(--bg-sidebar);
  padding: 2rem 2rem 1.75rem;
  border-radius: 1rem;
  width: 90%;
  max-width: 440px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    padding: 1.5rem 1.25rem 1.5rem;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  h2 {
    font-size: 1.25rem;
    margin: 0 0 1.25rem 0;
    font-family: 'Poppins', sans-serif;
    color: var(--accent-orange);
    font-weight: 900;
    text-align: left;
    letter-spacing: -0.02em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  z-index: 1;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  position: relative;
`;

const Label = styled.label`
  font-weight: 700;
  color: var(--text-gray);
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.1rem;
  font-family: 'Poppins', sans-serif;
`;

const Input = styled.input`
  padding: 0.6rem 0.9rem;
  background: #fff !important;
  border: 1px solid var(--card-border, #e0e6ed) !important;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  transition: border 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-orange) !important;
    box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.15);
  }

  &::placeholder {
    color: var(--text-muted) !important;
  }
`;

const ErrorMessage = styled.div`
  background: #FFF5F5;
  border: 1px solid #FFE3E3;
  color: #E03131;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  font-family: 'Poppins', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.25rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: stretch;
    gap: 0.5rem;
    margin-top: 1rem;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.6rem 1.25rem;
  border-radius: 0.5rem;
  border: 2px solid var(--accent-orange);
  background: ${props => props.$variant === 'primary' ? 'var(--accent-orange)' : 'transparent'};
  color: ${props => props.$variant === 'primary' ? '#fff' : 'var(--accent-orange)'};
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  min-height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  white-space: nowrap;
  box-shadow: ${props => props.$variant === 'primary' ? '0 2px 8px rgba(255, 150, 0, 0.3)' : 'none'};

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    padding: 0.6rem 1rem;
    min-height: 2.75rem;
  }

  &:hover {
    background: ${props => props.$variant === 'primary' ? 'var(--accent-orange-dark)' : 'var(--accent-orange)'};
    color: #fff;
    border-color: ${props => props.$variant === 'primary' ? 'var(--accent-orange-dark)' : 'var(--accent-orange)'};
    box-shadow: ${props => props.$variant === 'primary' ? '0 4px 12px rgba(255, 150, 0, 0.35)' : 'none'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  span {
    position: relative;
    z-index: 1;
  }

  &:disabled {
    opacity: 0.85;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingDotsWrap = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 1px;
`;
const Dot = styled.span<{ $delay: number }>`
  animation: dotBlink 1s ease-in-out infinite;
  animation-delay: ${props => props.$delay}ms;
  @keyframes dotBlink {
    0%, 60%, 100% { opacity: 0.3; }
    30% { opacity: 1; }
  }
`;

const ConnectByIDForm: React.FC<ConnectByIDFormProps> = ({ onClose, onSubmit, error, isConnecting }) => {
  const [athleteId, setAthleteId] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (athleteId.trim() && !isConnecting) {
      onSubmit(athleteId.trim());
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <h2>Connect Athlete by ID</h2>
        <Form onSubmit={handleSubmit}>
          {error && (
            <ErrorMessage>{error}</ErrorMessage>
          )}
          <FormGroup>
            <Label htmlFor="athleteId">Athlete ID</Label>
            <Input
              type="text"
              id="athleteId"
              name="athleteId"
              value={athleteId}
              onChange={(e) => setAthleteId(e.target.value)}
              placeholder="Enter athlete ID"
              required
              disabled={isConnecting}
            />
          </FormGroup>

          <ButtonGroup>
            <Button type="button" onClick={onClose}>
              <span>Cancel</span>
            </Button>
            <Button type="submit" $variant="primary" disabled={isConnecting}>
              <span>{isConnecting ? <>Connecting<LoadingDotsWrap><Dot $delay={0}>.</Dot><Dot $delay={150}>.</Dot><Dot $delay={300}>.</Dot></LoadingDotsWrap></> : 'Connect'}</span>
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ConnectByIDForm;

